// =====================================================
// src/pages/Projectbeheer.js
// vPRJ-069 - Projectteksten Opslaan/Sluiten schrijft direct naar database en sluit alleen na succes.
// vPRJ-068 - Plakken altijd neutraal Calibri 11; toolbarselectie en vervolgopmaak stabieler.
// vPRJ-067 - Documenteditor Calibri 11 pt / 1,15; behoud bestaande expliciete opmaak.
// vPRJ-066
// - Artikel in Projectbeheer kan alleen worden opgeslagen met zichtbare Offertetekst; opmaak-only zoals <br> wordt afgekeurd.
// - Overige projectfuncties en tekstregels blijven ongewijzigd.
// - Regeltype voortaan alleen Artikel/Tekst; oude Kop/Subtotaal/Totaal blijven leesbaar en worden bij opslaan als Tekst vastgelegd.
// - Projectkopieën met oude typen worden Tekst; bestaande projectinhoud en financiële logica behouden.
// vPRJ-064
// - Vergrote Offertetekst/Interne tekst: alleen Terug rechtsboven en rechtsonder; wijzigingen blijven in formulier bewaard.
// - Overbodige Opslaan-knop in vergrote editor verwijderd; normale vier knoppen en Projectteksten bewerken ongewijzigd.
// vPRJ-063
// - Ctrl+V in Offertetekst/Interne tekst (ook popup/vergroot) verwijdert Word/Outlook-opmaak.
// - Opmaakbalk, lege beginregels, projectregels en opslag ongewijzigd.
// vPRJ-062
// - Offertetekst en Interne tekst: handmatig ingevoerde blanco beginregels behouden bij verlaten, opslaan en opnieuw openen.
// - Geldt voor standaardveld, vergrote editor en projectteksten-popup; overige functies ongewijzigd.
// vPRJ-061
// - Standaard voor project-/offertetekst is Calibri 11 pt (bestaande expliciete opmaak intact).
// vPRJ-060
// - Lettertype-opmaak: selectie niet verliezen bij klikken in toolbar; voorkomt wisselvallig formatteren.
// - De teksteditor is gedeeld door Offertetekst en Interne tekst (incl. popup en vergroot veld).
// vPRJ-059
// - Opslaan bewaart en blijft bij geselecteerd project; Opslaan/Sluiten bewaart en gaat naar zoeken.
// - Vier knoppen in vast 2x2-blok; kopieerfunctie en bestaande velden blijven behouden.
// vPRJ-058
// - Eenheid toegevoegd bij artikelen, inclusief kopieerfuncties en opslag.
// - Rich-text: HTML-spaties en lege eerste regels genormaliseerd; bestaande opmaak behouden.
// vPRJ-057
// - FIX tekstinvoer: spatie en Backspace blijven normaal werken tijdens rich-text invoer.
// - FIX legacy HTML: oude <font face/size>-opmaak wordt bij laden genormaliseerd naar CSS.
// - Daardoor wordt bijvoorbeeld oud size="3" werkelijk als StaffIQ 11 pt weergegeven en niet alleen als 11 in de toolbar.
// - FIX: gekozen opmaak in een leeg tekstveld blijft actief tijdens het typen.
// - FIX tekstverwerker: in een leeg tekstveld kan vooraf opmaak worden gekozen; bij bestaande tekst blijft selectie verplicht.
// - Desktop: rechter invoerkolom blijft sticky staan; midden-grid en rechterkolom scrollen onafhankelijk
//
// Projectbeheer:
// - Beheer van projectonderdelen / bouwstenen
// - Onbeperkte lagen via parent_id
// - Nummering via code / full_code
// - Volledige naam via full_name
// - Midden grid met zoeken, filters en sortering
// - Rechter invoerkolom alleen actief na Nieuw of selectie
// - Popup voor offerte- en interne projectteksten
// - Spreekbasis aanwezig
// - Prijs exclusief/inclusief BTW rekenen direct naar elkaar om
// - Projectstructuur standaard ingeklapt met uitklappen/inklappen per niveau
// =====================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vPRJ-069";

const emptyForm = {
  id: null,
  copy_from_id: "",
  copy_children: false,
  parent_id: "",
  code: "",
  name: "",
  full_code: "",
  full_name: "",
  level: 1,
  sort_order: 0,
  offer_text: "",
  internal_text: "",
  line_type: "item",
  unit: "",
  price: "",
  price_incl_vat: "",
  vat_rate: "hoog",
  is_active: true,
};

function euro(v) {
  const n = Number(v || 0);
  return n.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
  });
}


const RICH_TEXT_ALLOWED_FONTS = [
  "Calibri",
  "Arial",
  "Verdana",
  "Times New Roman",
];

function escapeRichTextHtml(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function normalizeRichTextHtml(value = "") {
  const raw = String(value || "")
    // Oude editorcodes voor spaties mogen niet als tekst zichtbaar worden.
    .replace(/&(?:amp;)+nbsp;|&nbsp;|&#160;|&#x[aA]0;/gi, " ")
    .replace(/\u00a0/g, " ");
  if (!raw) return "";

  const looksLikeHtml = /<\/?(?:b|strong|i|em|u|br|div|p|span|font|ul|ol|li)\b/i.test(raw);
  if (!looksLikeHtml) {
    return escapeRichTextHtml(raw).replace(/\r?\n/g, "<br>");
  }

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return raw;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
  const root = doc.body.firstElementChild;

  const allowedTags = new Set([
    "B", "STRONG", "I", "EM", "U", "BR", "DIV", "P", "SPAN", "FONT", "UL", "OL", "LI", "UL", "OL", "LI",
  ]);

  function cleanNode(node) {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return;

      if (!allowedTags.has(child.tagName)) {
        child.replaceWith(...child.childNodes);
        return;
      }

      // vPRJ-057: oude HTML uit eerdere editors gebruikte <font face="..." size="...">.
      // Browser-size 3 is standaard ongeveer 12 pt, terwijl StaffIQ size 3 als 11 pt
      // presenteert. Daardoor kon de toolbar "11" tonen terwijl de tekst zichtbaar
      // groter was. Zet legacy FONT daarom meteen om naar moderne CSS op een SPAN.
      if (child.tagName === "FONT") {
        const legacyFace = String(child.getAttribute("face") || "")
          .replace(/["']/g, "")
          .trim();
        const legacySize = String(child.getAttribute("size") || "").trim();
        const legacySizePt = {
          "1": "8pt",
          "2": "10pt",
          "3": "11pt",
          "4": "14pt",
          "5": "18pt",
          "6": "24pt",
          "7": "36pt",
        }[legacySize] || "";

        const span = doc.createElement("span");
        if (RICH_TEXT_ALLOWED_FONTS.includes(legacyFace)) {
          span.style.fontFamily = legacyFace;
        }
        if (legacySizePt) {
          span.style.fontSize = legacySizePt;
        }

        while (child.firstChild) span.appendChild(child.firstChild);
        child.replaceWith(span);
        cleanNode(span);
        return;
      }

      [...child.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();

        if (child.tagName === "FONT" && (name === "face" || name === "size")) {
          if (name === "face" && !RICH_TEXT_ALLOWED_FONTS.includes(attr.value)) {
            child.removeAttribute(attr.name);
          }
          if (name === "size" && !/^[1-7]$/.test(attr.value)) {
            child.removeAttribute(attr.name);
          }
          return;
        }

        if (["SPAN", "DIV", "P", "LI"].includes(child.tagName) && name === "style") {
          const safeRules = String(attr.value || "")
            .split(";")
            .map((rule) => rule.trim())
            .filter(Boolean)
            .filter((rule) => {
              const lower = rule.toLowerCase();
              return (
                lower.startsWith("font-family:") ||
                lower.startsWith("font-size:") ||
                lower.startsWith("font-weight:") ||
                lower.startsWith("font-style:") ||
                lower.startsWith("text-decoration:") ||
                lower.startsWith("line-height:")
              );
            })
            .join("; ");

          if (safeRules) child.setAttribute("style", safeRules);
          else child.removeAttribute("style");
          return;
        }

        child.removeAttribute(attr.name);
      });

      cleanNode(child);
    });
  }

  cleanNode(root);
  // vPRJ-062: bewaart ook expliciet ingevoerde lege beginregels
  // (<br>, <div><br></div>, <p><br></p>), in plaats van deze weg te filteren.
  return root.innerHTML;
}

function richTextPlainText(value = "") {
  const html = normalizeRichTextHtml(value);

  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;
  return String(div.innerText || div.textContent || "").trim();
}

function RichTextToolbar({ editorRef, disabled = false, compact = false, onContentChange = null, toolbarInteractionRef = null }) {
  const [activeFont, setActiveFont] = useState("");
  const [activeSize, setActiveSize] = useState("");
  const [activeLineHeight, setActiveLineHeight] = useState("");
  const [activeBold, setActiveBold] = useState(false);
  const [activeItalic, setActiveItalic] = useState(false);
  const [activeUnderline, setActiveUnderline] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [editorIsEmpty, setEditorIsEmpty] = useState(false);
  const [pendingFont, setPendingFont] = useState("Calibri");
  const [pendingSize, setPendingSize] = useState("3");
  const [pendingLineHeight, setPendingLineHeight] = useState("1.15");
  const [pendingBold, setPendingBold] = useState(false);
  const [pendingItalic, setPendingItalic] = useState(false);
  const [pendingUnderline, setPendingUnderline] = useState(false);
  const savedRangeRef = useRef(null);
  const selectionSnapshotRef = useRef(null);

  function selectionIsInsideEditor(range) {
    const editor = editorRef?.current;
    if (!editor || !range) return false;

    const node = range.commonAncestorContainer;
    const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    return !!element && (element === editor || editor.contains(element));
  }

  function selectionHasText(range) {
    return !!range && !range.collapsed && String(range.toString() || "").length > 0;
  }

  function rememberEditorSelection() {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (selectionIsInsideEditor(range)) {
        savedRangeRef.current = range.cloneRange();
        if (selectionHasText(range)) selectionSnapshotRef.current = range.toString();
      }
    } catch {}
  }

  function restoreEditorSelection() {
    try {
      const range = savedRangeRef.current;
      if (!range) return;

      const selection = window.getSelection();
      if (!selection) return;

      selection.removeAllRanges();
      selection.addRange(range);
    } catch {}
  }

  function normalizeActiveFont(raw) {
    const value = String(raw || "")
      .replace(/["']/g, "")
      .trim()
      .toLowerCase();

    if (!value) return "";

    return (
      RICH_TEXT_ALLOWED_FONTS.find((font) => {
        const wanted = font.toLowerCase();
        return value === wanted || value.startsWith(`${wanted},`) || value.includes(wanted);
      }) || ""
    );
  }

  function fontSizeToCommandValue(raw) {
    const px = parseFloat(String(raw || ""));
    if (!Number.isFinite(px)) return "";
    if (px <= 13.6) return "2";   // ca. 10 pt
    if (px <= 16.6) return "3";   // ca. 11 pt
    if (px <= 21.5) return "4";   // ca. 14 pt
    return "5";                   // ca. 18 pt
  }

  function normalizeLineHeight(raw, element) {
    let value = parseFloat(String(raw || ""));
    if (!Number.isFinite(value) || value <= 0) return "";

    // getComputedStyle geeft line-height vaak in pixels terug.
    if (value > 4) {
      const fontSize = parseFloat(window.getComputedStyle(element).fontSize || "");
      if (Number.isFinite(fontSize) && fontSize > 0) value /= fontSize;
    }

    const allowed = [1, 1.15, 1.5, 2];
    let best = allowed[0];
    let diff = Math.abs(value - best);
    for (const candidate of allowed.slice(1)) {
      const d = Math.abs(value - candidate);
      if (d < diff) {
        best = candidate;
        diff = d;
      }
    }
    return diff <= 0.18 ? String(best) : "";
  }

  function collectSelectionElements(range) {
    const editor = editorRef?.current;
    if (!editor || !range || !selectionIsInsideEditor(range)) return [];

    const elements = new Set();

    const addElement = (node) => {
      const el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
      if (el && (el === editor || editor.contains(el))) elements.add(el);
    };

    addElement(range.startContainer);
    addElement(range.endContainer);

    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.nodeValue) continue;
      try {
        if (range.intersectsNode(node)) addElement(node);
      } catch {}
    }

    return [...elements];
  }

  function commonValue(values) {
    const clean = values.filter((v) => v !== null && v !== undefined && v !== "");
    if (!clean.length) return "";
    const first = String(clean[0]);
    return clean.every((v) => String(v) === first) ? first : "";
  }

  function updateActiveFormatting() {
    // Het native selectmenu kan de browserselectie tijdelijk laten instorten.
    // Tijdens een toolbar-actie daarom de eerder geselecteerde tekst bewaren.
    if (toolbarInteractionRef?.current) return;
    try {
      const editor = editorRef?.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (!selectionIsInsideEditor(range)) return;

      savedRangeRef.current = range.cloneRange();
      const selected = selectionHasText(range);
      if (selected) selectionSnapshotRef.current = range.toString();
      const empty = String(editor.innerText || editor.textContent || "").replace(/\u200B/g, "").trim() === "";
      setHasSelection(selected);
      setEditorIsEmpty(empty);

      // Zonder selectie: alleen tonen wat voor het HELE blok gelijk is.
      // Met selectie: alleen tonen wat binnen de SELECTIE gelijk is.
      let elements = [];
      if (selected) {
        elements = collectSelectionElements(range);
      } else {
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (String(node.nodeValue || "").length > 0) {
            const el = node.parentElement || editor;
            elements.push(el);
          }
        }
        if (!elements.length) elements = [editor];
      }

      const fonts = elements.map((el) =>
        normalizeActiveFont(window.getComputedStyle(el).fontFamily)
      );
      const sizes = elements.map((el) =>
        fontSizeToCommandValue(window.getComputedStyle(el).fontSize)
      );
      const lines = elements.map((el) =>
        normalizeLineHeight(window.getComputedStyle(el).lineHeight, el)
      );

      if (!empty) {
        setActiveFont(commonValue(fonts));
        setActiveSize(commonValue(sizes));
        setActiveLineHeight(commonValue(lines));
      }

      if (selected) {
        setActiveBold(document.queryCommandState("bold") === true);
        setActiveItalic(document.queryCommandState("italic") === true);
        setActiveUnderline(document.queryCommandState("underline") === true);
      } else {
        setActiveBold(false);
        setActiveItalic(false);
        setActiveUnderline(false);
      }
    } catch {}
  }

  useEffect(() => {
    const handleSelectionChange = () => updateActiveFormatting();

    document.addEventListener("selectionchange", handleSelectionChange);
    const timer = setTimeout(handleSelectionChange, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [editorRef]);

  function getSelectedRangeForChange() {
    restoreEditorSelection();

    const editor = editorRef?.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    if (!selectionIsInsideEditor(range)) return null;

    // Bestaande tekst: alleen een echte selectie mag worden gewijzigd.
    if (selectionHasText(range)) return range;

    // Leeg tekstveld: de cursor mag wel de opmaak voor nieuw te typen tekst bepalen.
    const empty = String(editor.innerText || editor.textContent || "").replace(/\u200B/g, "").trim() === "";
    if (range.collapsed && empty) return range;

    return null;
  }

  function prepareEmptyEditorTypingFormat(next = {}) {
    const editor = editorRef?.current;
    if (!editor || disabled) return false;

    const empty = String(editor.innerText || editor.textContent || "").replace(/\u200B/g, "").trim() === "";
    if (!empty) return false;

    const font = next.font ?? pendingFont ?? "Calibri";
    const size = next.size ?? pendingSize ?? "3";
    const lineHeight = next.lineHeight ?? pendingLineHeight ?? "1.15";
    const bold = next.bold ?? pendingBold;
    const italic = next.italic ?? pendingItalic;
    const underline = next.underline ?? pendingUnderline;

    setPendingFont(font);
    setPendingSize(size);
    setPendingLineHeight(lineHeight);
    setPendingBold(!!bold);
    setPendingItalic(!!italic);
    setPendingUnderline(!!underline);

    const sizePx = { "2": "13.333px", "3": "14.667px", "4": "18.667px", "5": "24px" }[String(size)] || "14.667px";

    editor.innerHTML = "";
    const span = document.createElement("span");
    span.style.fontFamily = font;
    span.style.fontSize = sizePx;
    span.style.lineHeight = lineHeight;
    span.style.fontWeight = bold ? "700" : "400";
    span.style.fontStyle = italic ? "italic" : "normal";
    span.style.textDecoration = underline ? "underline" : "none";

    const textNode = document.createTextNode("\u200B");
    span.appendChild(textNode);
    editor.appendChild(span);

    const range = document.createRange();
    range.setStart(textNode, 1);
    range.collapse(true);

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    savedRangeRef.current = range.cloneRange();

    editor.focus();
    setEditorIsEmpty(true);
    setHasSelection(false);
    setActiveFont(font);
    setActiveSize(String(size));
    setActiveLineHeight(String(lineHeight));

    return true;
  }

  function applyLineHeight(value) {
    if (disabled) return;

    try {
      const range = getSelectedRangeForChange();
      if (!range) return;

      const editor = editorRef?.current;

      // In een leeg veld geldt de gekozen regelhoogte voor de tekst die hierna wordt getypt.
      if (range.collapsed && editorIsEmpty) {
        prepareEmptyEditorTypingFormat({ lineHeight: value });
        return;
      }

      const touched = new Set();

      const addBlock = (node) => {
        let el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
        while (el && el !== editor) {
          if (["DIV", "P", "LI"].includes(el.tagName)) {
            touched.add(el);
            return;
          }
          el = el.parentElement;
        }
      };

      addBlock(range.startContainer);
      addBlock(range.endContainer);

      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        try {
          if (range.intersectsNode(node)) addBlock(node);
        } catch {}
      }

      // Geen blok-element aanwezig: omwikkel uitsluitend de selectie.
      if (!touched.size) {
        const span = document.createElement("span");
        span.style.lineHeight = value;
        try {
          range.surroundContents(span);
        } catch {
          const fragment = range.extractContents();
          span.appendChild(fragment);
          range.insertNode(span);
        }
      } else {
        touched.forEach((el) => {
          el.style.lineHeight = value;
        });
      }

      setActiveLineHeight(String(value || ""));
      if (editor && typeof onContentChange === "function") {
        onContentChange(normalizeRichTextHtml(editor.innerHTML));
      }

      rememberEditorSelection();
      setTimeout(() => {
        if (toolbarInteractionRef) toolbarInteractionRef.current = false;
        updateActiveFormatting();
      }, 0);
    } catch {}
  }

  function exec(command, value = null) {
    if (disabled) return;

    try {
      const range = getSelectedRangeForChange();
      if (!range) return;

      if (range.collapsed && editorIsEmpty) {
        if (command === "fontName") {
          prepareEmptyEditorTypingFormat({ font: value });
          return;
        }
        if (command === "fontSize") {
          prepareEmptyEditorTypingFormat({ size: value });
          return;
        }
        if (command === "bold") {
          prepareEmptyEditorTypingFormat({ bold: !pendingBold });
          return;
        }
        if (command === "italic") {
          prepareEmptyEditorTypingFormat({ italic: !pendingItalic });
          return;
        }
        if (command === "underline") {
          prepareEmptyEditorTypingFormat({ underline: !pendingUnderline });
          return;
        }
      }

      editorRef?.current?.focus?.();
      restoreEditorSelection();
      document.execCommand(command, false, value);

      const editor = editorRef?.current;
      if (editor && typeof onContentChange === "function") {
        onContentChange(normalizeRichTextHtml(editor.innerHTML));
      }

      rememberEditorSelection();
      setTimeout(() => {
        if (toolbarInteractionRef) toolbarInteractionRef.current = false;
        updateActiveFormatting();
      }, 0);
    } catch {}
  }

  const canFormat = hasSelection || editorIsEmpty;
  const selectionDisabled = disabled || !canFormat;

  const buttonStyle = {
    minWidth: 32,
    height: 30,
    padding: "0 8px",
    border: "1px solid #cfd8e3",
    borderRadius: 5,
    background: "#fff",
    cursor: selectionDisabled ? "not-allowed" : "pointer",
    opacity: selectionDisabled ? 0.5 : 1,
    fontSize: 13,
    fontWeight: 700,
  };

  return (
    <div
      className="rich-text-toolbar"
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: compact ? 5 : 7,
      }}
    >
      <select
        value={editorIsEmpty ? pendingFont : activeFont}
        disabled={selectionDisabled}
        onPointerDown={() => { rememberEditorSelection(); if (toolbarInteractionRef) toolbarInteractionRef.current = true; }}
        onMouseDown={(e) => {
          e.stopPropagation();
          rememberEditorSelection();
          if (toolbarInteractionRef) toolbarInteractionRef.current = true;
        }}
        onBlur={() => { setTimeout(() => { if (toolbarInteractionRef) toolbarInteractionRef.current = false; }, 0); }}
        onChange={(e) => {
          const value = e.target.value;
          if (value) exec("fontName", value);
        }}
        style={{
          height: 30,
          minWidth: 120,
          border: "1px solid #cfd8e3",
          borderRadius: 5,
          background: "#fff",
        }}
        title={hasSelection ? "Lettertype van selectie" : editorIsEmpty ? "Lettertype voor nieuwe tekst" : "Selecteer eerst tekst om het lettertype te wijzigen"}
      >
        <option value="">—</option>
        {RICH_TEXT_ALLOWED_FONTS.map((font) => (
          <option key={font} value={font}>{font}</option>
        ))}
      </select>

      <select
        value={editorIsEmpty ? pendingSize : activeSize}
        disabled={selectionDisabled}
        onPointerDown={() => { rememberEditorSelection(); if (toolbarInteractionRef) toolbarInteractionRef.current = true; }}
        onMouseDown={(e) => {
          e.stopPropagation();
          rememberEditorSelection();
          if (toolbarInteractionRef) toolbarInteractionRef.current = true;
        }}
        onBlur={() => { setTimeout(() => { if (toolbarInteractionRef) toolbarInteractionRef.current = false; }, 0); }}
        onChange={(e) => {
          const value = e.target.value;
          if (value) exec("fontSize", value);
        }}
        style={{
          height: 30,
          minWidth: 100,
          border: "1px solid #cfd8e3",
          borderRadius: 5,
          background: "#fff",
        }}
        title={hasSelection ? "Tekstgrootte van selectie" : editorIsEmpty ? "Tekstgrootte voor nieuwe tekst" : "Selecteer eerst tekst om de tekstgrootte te wijzigen"}
      >
        <option value="">—</option>
        <option value="2">10</option>
        <option value="3">11</option>
        <option value="4">14</option>
        <option value="5">18</option>
      </select>

      <select
        value={editorIsEmpty ? pendingLineHeight : activeLineHeight}
        disabled={selectionDisabled}
        onPointerDown={() => { rememberEditorSelection(); if (toolbarInteractionRef) toolbarInteractionRef.current = true; }}
        onMouseDown={(e) => { e.stopPropagation(); rememberEditorSelection(); if (toolbarInteractionRef) toolbarInteractionRef.current = true; }}
        onBlur={() => { setTimeout(() => { if (toolbarInteractionRef) toolbarInteractionRef.current = false; }, 0); }}
        onChange={(e) => {
          const value = e.target.value;
          if (value) applyLineHeight(value);
        }}
        style={{ height: 30, minWidth: 125, border: "1px solid #cfd8e3", borderRadius: 5, background: "#fff" }}
        title={hasSelection ? "Regelhoogte van selectie" : editorIsEmpty ? "Regelhoogte voor nieuwe tekst" : "Selecteer eerst tekst om de regelhoogte te wijzigen"}
      >
        <option value="">—</option>
        <option value="1">Regel 1,0</option>
        <option value="1.15">Regel 1,15</option>
        <option value="1.5">Regel 1,5</option>
        <option value="2">Regel 2,0</option>
      </select>

      <button
        type="button"
        disabled={selectionDisabled}
        style={{
          ...buttonStyle,
          background: (hasSelection && activeBold) || (editorIsEmpty && pendingBold) ? "#dbeafe" : "#fff",
          borderColor: (hasSelection && activeBold) || (editorIsEmpty && pendingBold) ? "#2f7dd1" : "#cfd8e3",
          color: (hasSelection && activeBold) || (editorIsEmpty && pendingBold) ? "#0b4f7a" : "#111",
          boxShadow: (hasSelection && activeBold) || (editorIsEmpty && pendingBold) ? "inset 0 0 0 1px #2f7dd1" : "none",
        }}
        onMouseDown={(e) => { e.preventDefault(); rememberEditorSelection(); exec("bold"); }}
        title={hasSelection ? (activeBold ? "Vet is actief" : "Vet") : "Selecteer eerst tekst"}
        aria-pressed={(hasSelection && activeBold) || (editorIsEmpty && pendingBold)}
      >
        B
      </button>

      <button
        type="button"
        disabled={selectionDisabled}
        style={{
          ...buttonStyle,
          fontStyle: "italic",
          background: (hasSelection && activeItalic) || (editorIsEmpty && pendingItalic) ? "#dbeafe" : "#fff",
          borderColor: (hasSelection && activeItalic) || (editorIsEmpty && pendingItalic) ? "#2f7dd1" : "#cfd8e3",
          color: (hasSelection && activeItalic) || (editorIsEmpty && pendingItalic) ? "#0b4f7a" : "#111",
          boxShadow: (hasSelection && activeItalic) || (editorIsEmpty && pendingItalic) ? "inset 0 0 0 1px #2f7dd1" : "none",
        }}
        onMouseDown={(e) => { e.preventDefault(); rememberEditorSelection(); exec("italic"); }}
        title={hasSelection ? (activeItalic ? "Cursief is actief" : "Cursief") : "Selecteer eerst tekst"}
        aria-pressed={(hasSelection && activeItalic) || (editorIsEmpty && pendingItalic)}
      >
        I
      </button>

      <button
        type="button"
        disabled={selectionDisabled}
        style={{
          ...buttonStyle,
          textDecoration: "underline",
          background: (hasSelection && activeUnderline) || (editorIsEmpty && pendingUnderline) ? "#dbeafe" : "#fff",
          borderColor: (hasSelection && activeUnderline) || (editorIsEmpty && pendingUnderline) ? "#2f7dd1" : "#cfd8e3",
          color: (hasSelection && activeUnderline) || (editorIsEmpty && pendingUnderline) ? "#0b4f7a" : "#111",
          boxShadow: (hasSelection && activeUnderline) || (editorIsEmpty && pendingUnderline) ? "inset 0 0 0 1px #2f7dd1" : "none",
        }}
        onMouseDown={(e) => { e.preventDefault(); rememberEditorSelection(); exec("underline"); }}
        title={hasSelection ? (activeUnderline ? "Onderstrepen is actief" : "Onderstrepen") : "Selecteer eerst tekst"}
        aria-pressed={(hasSelection && activeUnderline) || (editorIsEmpty && pendingUnderline)}
      >
        U
      </button>

      <button
        type="button"
        disabled={selectionDisabled}
        style={{ ...buttonStyle, minWidth: 42 }}
        onMouseDown={(e) => { e.preventDefault(); rememberEditorSelection(); exec("insertUnorderedList"); }}
        title={hasSelection ? "Opsomming met bullets" : "Selecteer eerst tekst"}
      >
        •
      </button>

      <button
        type="button"
        disabled={selectionDisabled}
        style={{ ...buttonStyle, minWidth: 42 }}
        onMouseDown={(e) => { e.preventDefault(); rememberEditorSelection(); exec("insertOrderedList"); }}
        title={hasSelection ? "Genummerde lijst" : "Selecteer eerst tekst"}
      >
        1.
      </button>
    </div>
  );
}

// vWORD-PASTE: plak uit Word/Outlook uitsluitend klembordtekst, zonder verborgen Office-opmaak.
// Behoud zichtbare tekst, tabs en alle bewuste nieuwe/lege regels.
function cleanWordClipboardText(value = "") {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u0085\u2028\u2029]/g, "\n")
    .replace(/[\u00a0\u2000-\u200a\u202f\u205f\u3000]/g, " ")
    // Onzichtbare afbreek-/richtings-/nulbreedtetekens uit Word verwijderen.
    .replace(/[\u00ad\u034f\u061c\u180e\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff]/g, "")
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/g, "");
}

// Houd tekst tegen de bovenrand; standaard <p>-marges geven anders een schijnbare lege regel.
const STAFFIQ_RICH_TEXT_TOP_CSS = `.staffiq-rich-text-surface > :first-child { margin-top: 0 !important; }
.staffiq-rich-text-surface > :last-child { margin-bottom: 0 !important; }`;
function RichTextEditor({
  value,
  onChange,
  disabled = false,
  minHeight = 110,
  placeholder = "",
  toolbarVisible = true,
  onActivate = null,
  externalRef = null,
}) {
  const editorRef = useRef(null);
  const toolbarInteractionRef = useRef(false);

  function setEditorRefs(el) {
    editorRef.current = el;
    if (externalRef && typeof externalRef === "object") {
      externalRef.current = el;
    }
  }

  useEffect(() => {
    if (!editorRef.current) return;

    // Tijdens typen beheert contentEditable zelf de DOM en cursor.
    // React mag de inhoud dan NIET opnieuw invullen na iedere toetsaanslag.
    // Dit is dezelfde werkende oplossing als in Offertes vOFF-125.
    if (document.activeElement === editorRef.current) return;

    const normalized = normalizeRichTextHtml(value);
    if (editorRef.current.innerHTML !== normalized) {
      editorRef.current.innerHTML = normalized;
    }
  }, [value]);

  return (
    <div>
      <style>{STAFFIQ_RICH_TEXT_TOP_CSS}</style>
      {toolbarVisible && (
        <RichTextToolbar editorRef={editorRef} disabled={disabled} onContentChange={onChange} toolbarInteractionRef={toolbarInteractionRef} />
      )}
      <div
        ref={setEditorRefs}
        className="staffiq-rich-text-surface"
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        data-placeholder={placeholder}
        onPaste={(e) => {
          if (disabled) return;
          // Alleen platte tekst uit het klembord; geen Word-HTML of meegekomen fonts.
          e.preventDefault();
          const pasted = cleanWordClipboardText(e.clipboardData?.getData("text/plain") || "");
          if (!pasted) return;

          const editor = e.currentTarget;
          editor.focus();
          // Plak onafhankelijk van de opmaak op de invoegpositie. Zo maakt een
          // vet/cursief eerste woord niet onbedoeld de hele geplakte tekst vet.
          const cleanHtml = escapeRichTextHtml(pasted).replace(/\n/g, "<br>");
          const neutralHtml = `<span style="font-family:Calibri;font-size:11pt;line-height:1.15;font-weight:400;font-style:normal;text-decoration:none">${cleanHtml}</span>`;
          const done = document.execCommand("insertHTML", false, neutralHtml);
          if (!done) document.execCommand("insertText", false, pasted);
          onChange(editor.innerHTML);
        }}
        onMouseDown={() => onActivate?.()}
        onFocus={() => onActivate?.()}
        onInput={(e) => {
          const el = e.currentTarget;

          // Zero-width marker alleen verwijderen zodra echte tekst aanwezig is.
          if (String(el.innerText || el.textContent || "").replace(/\u200B/g, "").length > 0) {
            const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
            let node;
            while ((node = walker.nextNode())) {
              if (node.nodeValue?.includes("\u200B")) {
                node.nodeValue = node.nodeValue.replace(/\u200B/g, "");
              }
            }
          }

          // Tijdens invoer browser-HTML rechtstreeks bewaren.
          // Normaliseren pas bij verlaten van het veld.
          onChange(el.innerHTML);
        }}
        onBlur={(e) => {
          // vPRJ-060 / vOFF-135: kies je boven dit tekstveld een lettertype,
          // dan mag normaliseren de geselecteerde DOM-nodes niet vervangen.
          // Anders verwijst de opgeslagen tekstselectie naar oude nodes.
          const nextFocus = e.relatedTarget;
          const toolbar = nextFocus?.closest?.(".rich-text-toolbar");
          if (toolbarInteractionRef.current || (toolbar && toolbar.parentElement === e.currentTarget.parentElement)) return;
          const cleaned = normalizeRichTextHtml(e.currentTarget.innerHTML).replace(/\u200B/g, "");
          if (e.currentTarget.innerHTML !== cleaned) {
            e.currentTarget.innerHTML = cleaned;
          }
          onChange(cleaned);
        }}
        style={{
          minHeight,
          padding: "9px 10px",
          border: "1px solid #d9d9d9",
          borderRadius: 5,
          background: disabled ? "#f7f7f7" : "#fff",
          boxSizing: "border-box",
          fontFamily: "Calibri, Carlito, Arial, sans-serif",
          fontSize: "11pt",
          lineHeight: 1.15,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
    </div>
  );
}


// ---------------- component ----------------


function norm(v) {
  return String(v || "").toLowerCase().trim();
}
function lineTypeLabel(value) {
  const t = String(value || "item").toLowerCase();

  if (t === "text") return "Tekst";
  if (t === "heading") return "Kop";
  if (t === "subtotal") return "Subtotaal";
  if (t === "total") return "Totaal";

  return "Artikel";
}

function isAmountLineType(value) {
  const t = String(value || "item").toLowerCase();
  return t === "item";
}

function vatPercentage(value) {
  const v = String(value || "hoog").toLowerCase();

  if (v === "laag") return 9;
  if (v === "0" || v === "verlegd") return 0;

  return 21;
}

function parsePriceInput(value) {
  const raw = String(value ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return null;

  let cleaned = raw;

  if (cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    const dotCount = (cleaned.match(/\./g) || []).length;
    if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function formatPriceInput(value) {
  const n = typeof value === "number" ? value : parsePriceInput(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2).replace(".", ",");
}

function priceInclFromExcl(priceExcl, vatRate) {
  const excl = parsePriceInput(priceExcl);
  if (!Number.isFinite(excl)) return "";

  const factor = 1 + vatPercentage(vatRate) / 100;
  return formatPriceInput(excl * factor);
}

function priceExclFromIncl(priceIncl, vatRate) {
  const incl = parsePriceInput(priceIncl);
  if (!Number.isFinite(incl)) return "";

  const factor = 1 + vatPercentage(vatRate) / 100;
  return formatPriceInput(factor ? incl / factor : incl);
}


async function api(url, options = {}) {
  const token = sessionStorage.getItem("token") || localStorage.getItem("token");

  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}${txt ? " - " + txt : ""}`);
  }

  return res.json();
}

export default function Projectbeheer() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selectedId, setSelectedId] = useState(null);

  const [zoek, setZoek] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [filterNaam, setFilterNaam] = useState("");
  const [filterParent, setFilterParent] = useState("");
  const [filterLaag, setFilterLaag] = useState("");
  const [filterActief, setFilterActief] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());

const [sort, setSort] = useState({ key: "full_code", dir: "asc" });
  const [mode, setMode] = useState("search");

const [loading, setLoading] = useState(false);
  const [melding, setMelding] = useState("");
  const [fout, setFout] = useState("");

  const [speechInfo, setSpeechInfo] = useState("");
  const [hideVoiceError, setHideVoiceError] = useState(false);
  const prevVoiceErrorRef = useRef("");

const [tekstPopupOpen, setTekstPopupOpen] = useState(false);
  const [tekstPopupSaving, setTekstPopupSaving] = useState(false);
  const [tekstPopupError, setTekstPopupError] = useState("");
const [expandedTextField, setExpandedTextField] = useState(""); // "offer_text" | "internal_text"

const pageRef = useRef(null);
  const midScrollRef = useRef(null);
  const rightPanelRef = useRef(null);
  // vPRJ-044: oorspronkelijke waarden van de geselecteerde regel bewaren voor Annuleren.
  const originalFormRef = useRef(null);

  const zoekRef = useRef(null);
  const naamRef = useRef(null);
  const codeRef = useRef(null);
  const prijsRef = useRef(null);
  const prijsInclRef = useRef(null);
  const lastPriceSourceRef = useRef("excl");
  const offerteTekstRef = useRef(null);
  const interneTekstRef = useRef(null);
  const offerteTekstBlokRef = useRef(null);
  const interneTekstBlokRef = useRef(null);

  const popupOfferteTekstRef = useRef(null);
  const popupInterneTekstRef = useRef(null);

  // Onthoudt welk veld het laatst actief was.
  // Dit is nodig omdat de knop Spreek in het midden staat,
  // terwijl je vaak rechts wilt dicteren.
  const lastActiveFieldRef = useRef("zoek");

  const [tekstDraft, setTekstDraft] = useState({
    offer_text: "",
    internal_text: "",
  });

const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState("");

  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiSource, setAiSource] = useState("page");
  const aiTextRef = useRef(null);

  const rawUser = sessionStorage.getItem("user") || localStorage.getItem("user") || "";
  let role = "user";
  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

const isReadonly = String(role).toLowerCase() === "readonly";
  const isFormLocked = mode !== "form";
  const isFormDisabled = isReadonly || isFormLocked;

const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6500,

    onCommand: (cmd, rawHeard) => {
      const raw = String(rawHeard || "").trim();

      if (raw) {
        setSpeechInfo(`🎙 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }

      if (raw && handleVoiceCommand(raw)) {
        return true;
      }

      if (!cmd || !cmd.type) {
        return false;
      }

      if (cmd.type === "new" || cmd.type === "nieuw") {
        nieuw();
        return true;
      }

      if (cmd.type === "refresh") {
        vernieuw();
        return true;
      }

      if (cmd.type === "save") {
        opslaan();
        return true;
      }

      if (cmd.type === "delete") {
        verwijderen();
        return true;
      }

      return false;
    },

    onDictation: (text) => {
      const raw = String(text || "").trim();

      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      if (handleVoiceCommand(raw)) {
        return;
      }

      applyDictationToProjectField(raw);
    },
  });

  useEffect(() => {
    const cur = String(voice.error || "");
    const prev = String(prevVoiceErrorRef.current || "");

    if (cur && cur !== prev) {
      setHideVoiceError(false);
      prevVoiceErrorRef.current = cur;
    }

    if (!cur) {
      prevVoiceErrorRef.current = "";
    }
  }, [voice.error]);

function detectActiveProjectField() {
    const el = document.activeElement;

    if (el === zoekRef.current) {
      lastActiveFieldRef.current = "zoek";
      return;
    }

    if (el === codeRef.current) {
      lastActiveFieldRef.current = "code";
      return;
    }

    if (el === naamRef.current) {
      lastActiveFieldRef.current = "naam";
      return;
    }

    if (el === prijsRef.current) {
      lastActiveFieldRef.current = "prijs";
      return;
    }

    if (el === prijsInclRef.current) {
      lastActiveFieldRef.current = "prijs_incl";
      return;
    }

    if (el === offerteTekstRef.current) {
      lastActiveFieldRef.current = "offer_text";
      return;
    }

    if (el === interneTekstRef.current) {
      lastActiveFieldRef.current = "internal_text";
      return;
    }

    if (el === popupOfferteTekstRef.current) {
      lastActiveFieldRef.current = "popup_offer_text";
      return;
    }

    if (el === popupInterneTekstRef.current) {
      lastActiveFieldRef.current = "popup_internal_text";
      return;
    }
  }

function maakZinnenNetjes(text) {
    let txt = String(text || "");

    txt = txt
      .replace(/\s+([.,!?;:])/g, "$1")
      .replace(/([.,!?;:])(?=\S)/g, "$1 ");

    txt = txt.replace(/(^|[.!?]\s+|\n+)([a-zà-ÿ])/g, (match, prefix, letter) => {
      return prefix + String(letter || "").toUpperCase();
    });

    return txt;
  }

  function appendText(current, raw) {
    const cur = String(current || "");
    const txt = nlText(raw);

    if (!txt) return cur;

    const isPunctuationOnly = /^[.,!?;:]$/.test(txt);

    let out = "";

    if (isPunctuationOnly) {
      out = `${cur}${txt}`;
    } else {
      const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
      out = `${cur}${sep}${txt}`;
    }

    return maakZinnenNetjes(out).trim();
  }

  function applyDictationToProjectField(raw) {
    const target = lastActiveFieldRef.current || "zoek";

    if (tekstPopupOpen) {
      if (target === "popup_offer_text") {
        setTekstDraft((p) => ({
          ...p,
          offer_text: appendText(p.offer_text, raw),
        }));
        return true;
      }

      if (target === "popup_internal_text") {
        setTekstDraft((p) => ({
          ...p,
          internal_text: appendText(p.internal_text, raw),
        }));
        return true;
      }
    }

    if (target === "zoek") {
      setZoek(raw);
      setMode("search");
      return true;
    }

    if (isFormDisabled) {
      return false;
    }

    if (target === "code") {
      setForm((p) => ({
        ...p,
        code: raw.replace(/\s+/g, ""),
      }));
      return true;
    }

    if (target === "naam") {
      setForm((p) => ({
        ...p,
        name: nlText(raw),
      }));
      return true;
    }

    if (target === "prijs") {
      lastPriceSourceRef.current = "excl";
      const value = raw.replace(/[^\d,.-]/g, "");
      setForm((p) => ({
        ...p,
        price: value,
        price_incl_vat: priceInclFromExcl(value, p.vat_rate),
      }));
      return true;
    }

    if (target === "prijs_incl") {
      lastPriceSourceRef.current = "incl";
      const value = raw.replace(/[^\d,.-]/g, "");
      setForm((p) => ({
        ...p,
        price_incl_vat: value,
        price: priceExclFromIncl(value, p.vat_rate),
      }));
      return true;
    }

    if (target === "offer_text") {
      setForm((p) => ({
        ...p,
        offer_text: appendText(p.offer_text, raw),
      }));
      return true;
    }

    if (target === "internal_text") {
      setForm((p) => ({
        ...p,
        internal_text: appendText(p.internal_text, raw),
      }));
      return true;
    }

    setZoek(raw);
    setMode("search");
    return true;
  }

  function startSpeak() {
    if (!voice.supported || voice.listening) return;

    detectActiveProjectField();

    setHideVoiceError(true);
    voice.startOnce();
  }

  useEffect(() => {
    laad();
  }, []);

async function laad() {
    setLoading(true);
    setFout("");

    try {
      const data = await api("/api/projectmanagement");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setFout(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

function positioneerBovenOpZoek() {
    setTimeout(() => {
      try {
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        zoekRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  function positioneerBovenZonderZoekFocus() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        if (pageRef.current) {
          pageRef.current.scrollIntoView?.({
            behavior: "auto",
            block: "start",
            inline: "nearest",
          });
        }
      } catch {}
    }, 0);
  }

  function positioneerOpRechterKolomMobiel() {
    if (window.innerWidth > 768) return;

    setTimeout(() => {
      try {
        const panel = rightPanelRef.current;
        if (!panel) return;

        panel.scrollTop = 0;

        panel.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

  async function vernieuw() {
    setZoek("");
    setFilterCode("");
    setFilterNaam("");
    setFilterParent("");
    setFilterLaag("");
    setFilterActief("");
    setExpandedIds(new Set());
    setForm({ ...emptyForm });
    setSelectedId(null);
    setMelding("");
    setFout("");
    setMode("search");
    setExpandedTextField("");

    await laad();

    positioneerBovenOpZoek();
  }

  function nieuw() {
    originalFormRef.current = { ...emptyForm };
    setForm({ ...emptyForm });
    setSelectedId(null);
    setMelding("");
    setFout("");
    setMode("form");
    setExpandedTextField("");
  }

  async function selecteer(item) {
    setExpandedTextField("");
    lastPriceSourceRef.current = "excl";
    setSelectedId(item.id);
    setFout("");
    setMelding("");
    setMode("form");

    positioneerOpRechterKolomMobiel();

    try {
      const data = await api(`/api/projectmanagement/${item.id}`);
      const loadedForm = {
        ...emptyForm,
        ...data,
        parent_id: data.parent_id ? String(data.parent_id) : "",
        price: data.price ?? "",
        price_incl_vat: priceInclFromExcl(data.price ?? "", data.vat_rate || "hoog"),
        is_active: data.is_active !== false,
      };

      originalFormRef.current = { ...loadedForm };
      setForm(loadedForm);

      positioneerOpRechterKolomMobiel();
    } catch (e) {
      setFout(e.message || String(e));
    }
  }

function annuleren() {
    setFout("");
    setMelding("");

    if (originalFormRef.current) {
      setForm({ ...originalFormRef.current });
      lastPriceSourceRef.current = "excl";
    } else {
      setForm({ ...emptyForm });
    }

    setMelding("De oorspronkelijke gegevens zijn teruggezet.");

    // Bewust selectedId, expandedIds, filters, sortering en scrollpositie niet wijzigen.
    // Daardoor blijft de gebruiker op exact hetzelfde projectonderdeel en blijven niveaus open.
  }

async function opslaan({ closeAfter = false } = {}) {
    if (isReadonly) return;

    setFout("");
    setMelding("");

    if (!String(form.code || "").trim()) {
      setFout("Nummer/code is verplicht.");
      positioneerBovenOpZoek();
      return;
    }

    if (!String(form.name || "").trim()) {
      setFout("Naam is verplicht.");
      positioneerBovenOpZoek();
      return;
    }

    // vPRJ-066: een artikel moet een echte, zichtbare offertetekst hebben.
    // Een lege rich-text-editor bevat soms alleen <br> of opmaakspaties.
    if (isAmountLineType(form.line_type) && !richTextPlainText(form.offer_text)) {
      setFout("Vul bij een artikel eerst Offertetekst in. Alleen een lege regel of opmaak is geen omschrijving.");
      return;
    }

    const isNieuwOnderdeel = !form.id;
    const bronIdVoorKinderen = form.copy_from_id || "";
    const kopieerKinderen = form.copy_children === true;

    // vPRJ-066: ook bij het kopiëren van een oude projectboom mogen geen
    // artikelkinderen zonder Offertetekst opnieuw ontstaan. Controleer VOOR
    // het opslaan van de nieuwe hoofdregel, zodat geen halve kopie overblijft.
    if (isNieuwOnderdeel && kopieerKinderen && bronIdVoorKinderen) {
      const pending = [String(bronIdVoorKinderen)];
      const visited = new Set();
      const invalid = [];
      while (pending.length) {
        const parent = pending.shift();
        if (visited.has(parent)) continue;
        visited.add(parent);
        const children = items.filter((item) => String(item.parent_id || "") === parent);
        for (const child of children) {
          if (isAmountLineType(child.line_type) && !richTextPlainText(child.offer_text)) {
            invalid.push(String(child.full_code || child.code || child.name || child.id));
          }
          pending.push(String(child.id));
        }
      }
      if (invalid.length) {
        setFout(`Kopiëren gestopt: vul eerst Offertetekst in bij artikel(en): ${invalid.join(", ")}.`);
        return;
      }
    }

    const exactPrice = (() => {
      if (lastPriceSourceRef.current === "incl") {
        const incl = parsePriceInput(form.price_incl_vat);
        const factor = 1 + vatPercentage(form.vat_rate) / 100;
        if (Number.isFinite(incl)) {
          return Number((factor ? incl / factor : incl).toFixed(6));
        }
      }

      const excl = parsePriceInput(form.price);
      return Number.isFinite(excl) ? Number(excl.toFixed(6)) : 0;
    })();

    const payload = {
      parent_id: form.parent_id ? Number(form.parent_id) : null,
      code: String(form.code || "").trim(),
      name: String(form.name || "").trim(),
      offer_text: form.offer_text || "",
      internal_text: form.internal_text || "",
      line_type: isAmountLineType(form.line_type) ? "item" : "text",
      unit: isAmountLineType(form.line_type) ? String(form.unit || "").trim() : "",
      price: exactPrice,
      vat_rate: form.vat_rate || "hoog",
      is_active: form.is_active !== false,
      sort_order: Number(form.sort_order || 0),
    };

    try {
      const saved = form.id
        ? await api(`/api/projectmanagement/${form.id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
          })
        : await api("/api/projectmanagement", {
            method: "POST",
            body: JSON.stringify(payload),
          });

      if (
        isNieuwOnderdeel &&
        kopieerKinderen &&
        bronIdVoorKinderen &&
        saved?.id
      ) {
        await kopieerOnderliggendeOnderdelen({
          bronParentId: bronIdVoorKinderen,
          nieuweParentId: saved.id,
        });
      }

      if (closeAfter) {
        setForm({ ...emptyForm });
        setSelectedId(null);
        setExpandedIds(new Set());
        setMode("search");
      } else {
        // Op hetzelfde onderdeel blijven, ook als het zojuist voor het eerst is aangemaakt.
        const fresh = {
          ...form,
          ...(saved && typeof saved === "object" ? saved : {}),
          id: saved?.id || form.id,
          parent_id: saved?.parent_id ? String(saved.parent_id) : form.parent_id,
          price: saved?.price ?? exactPrice,
          price_incl_vat: priceInclFromExcl(saved?.price ?? exactPrice, saved?.vat_rate || form.vat_rate),
          copy_from_id: "",
          copy_children: false,
        };
        originalFormRef.current = { ...fresh };
        setForm(fresh);
        setSelectedId(fresh.id);
        setMode("form");
      }

      if (isNieuwOnderdeel && kopieerKinderen && bronIdVoorKinderen) {
        setMelding("Opgeslagen inclusief onderliggende onderdelen ✅");
      } else {
        setMelding("Opgeslagen ✅");
      }

      await laad();
      if (closeAfter) positioneerBovenOpZoek();
    } catch (e) {
      setFout(e.message || String(e));
      positioneerBovenOpZoek();
    }
  }

async function verwijderen() {
    if (isReadonly || !form.id) return;

    if (!window.confirm("Weet je zeker dat je dit projectonderdeel wilt verwijderen?")) {
      return;
    }

    try {
      await api(`/api/projectmanagement/${form.id}`, {
        method: "DELETE",
        body: JSON.stringify({}),
      });

      setForm(emptyForm);
      setSelectedId(null);
      setExpandedIds(new Set());
      setMode("search");
      setMelding("Verwijderd ✅");

      await laad();

      positioneerBovenOpZoek();
    } catch (e) {
      setFout(e.message || String(e));
    }
  }

  function parentNaam(parentId) {
    if (!parentId) return "";
    const p = items.find((x) => String(x.id) === String(parentId));
    if (!p) return "";
    return `${p.full_code || p.code || ""} ${p.name || ""}`.trim();
  }

  const childIdsByParent = useMemo(() => {
    const map = new Map();

    for (const item of items) {
      const parentKey = item.parent_id ? String(item.parent_id) : "";
      if (!map.has(parentKey)) map.set(parentKey, []);
      map.get(parentKey).push(String(item.id));
    }

    return map;
  }, [items]);

  function hasChildren(itemId) {
    return (childIdsByParent.get(String(itemId)) || []).length > 0;
  }

  function toggleExpanded(itemId) {
    const key = String(itemId);

    setExpandedIds((prev) => {
      const next = new Set(prev);

      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });
  }

  function isTreeFilterActive() {
    return !!(
      String(zoek || "").trim() ||
      filterCode ||
      filterNaam ||
      filterParent ||
      filterLaag ||
      filterActief
    );
  }

  function hasVisibleAncestorPath(item) {
    let parentId = item?.parent_id ? String(item.parent_id) : "";
    const guard = new Set();

    while (parentId) {
      if (guard.has(parentId)) return false;
      guard.add(parentId);

      if (!expandedIds.has(parentId)) return false;

      const parent = items.find((x) => String(x.id) === parentId);
      if (!parent) break;

      parentId = parent.parent_id ? String(parent.parent_id) : "";
    }

    return true;
  }

  const optiesCode = useMemo(
    () => [...new Set(items.map((x) => x.full_code || x.code).filter(Boolean))].sort(),
    [items]
  );

  const optiesNaam = useMemo(
    () => [...new Set(items.map((x) => x.name).filter(Boolean))].sort(),
    [items]
  );

  const optiesParent = useMemo(
    () => [...new Set(items.map((x) => parentNaam(x.parent_id)).filter(Boolean))].sort(),
    [items]
  );

  const optiesLaag = useMemo(
    () => [...new Set(items.map((x) => String(x.level || 1)).filter(Boolean))].sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const z = norm(zoek);

    return items.filter((x) => {
      const code = x.full_code || x.code || "";
      const naam = x.name || "";
      const fullName = x.full_name || "";
      const parent = parentNaam(x.parent_id);
      const laag = String(x.level || 1);
      const actief = x.is_active === false ? "Nee" : "Ja";

      const hay = norm(`${code} ${naam} ${fullName} ${parent} ${x.offer_text || ""}`);

      if (z && !hay.includes(z)) return false;
      if (filterCode && code !== filterCode) return false;
      if (filterNaam && naam !== filterNaam) return false;
      if (filterParent && parent !== filterParent) return false;
      if (filterLaag && laag !== filterLaag) return false;
      if (filterActief && actief !== filterActief) return false;

      return true;
    });
  }, [items, zoek, filterCode, filterNaam, filterParent, filterLaag, filterActief]);

const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "desc" ? -1 : 1;

    const compareItems = (a, b) => {
      let av = "";
      let bv = "";

      if (sort.key === "full_code") {
        av = a.full_code || a.code || "";
        bv = b.full_code || b.code || "";
      }

      if (sort.key === "name") {
        av = a.name || "";
        bv = b.name || "";
      }

      if (sort.key === "parent") {
        av = parentNaam(a.parent_id);
        bv = parentNaam(b.parent_id);
      }

      if (sort.key === "level") {
        return dir * (Number(a.level || 1) - Number(b.level || 1));
      }

      if (sort.key === "price") {
        return dir * (Number(a.price || 0) - Number(b.price || 0));
      }

      if (sort.key === "active") {
        av = a.is_active === false ? "Nee" : "Ja";
        bv = b.is_active === false ? "Nee" : "Ja";
      }

      const primary = String(av).localeCompare(String(bv), "nl", {
        numeric: true,
        sensitivity: "base",
      });

      if (primary !== 0) return dir * primary;

      return String(a.id || "").localeCompare(String(b.id || ""), "nl", {
        numeric: true,
      });
    };

    arr.sort(compareItems);
    return arr;
  }, [filtered, sort, items]);

  const visibleSorted = useMemo(() => {
    // Zodra er gezocht/gefilterd wordt, tonen we de gevonden regels rechtstreeks.
    // Zonder filters bouwen we een echte parent/child-boom op. Daardoor staat ieder
    // kind altijd direct onder zijn bovenliggende onderdeel, ongeacht full_code/level.
    if (isTreeFilterActive()) {
      return sorted.map((item) => ({
        ...item,
        _treeLevel: Math.max(1, Number(item.level || 1)),
      }));
    }

    const byId = new Map(items.map((item) => [String(item.id), item]));
    const childrenByParent = new Map();

    for (const item of items) {
      const parentKey =
        item.parent_id && byId.has(String(item.parent_id))
          ? String(item.parent_id)
          : "";

      if (!childrenByParent.has(parentKey)) {
        childrenByParent.set(parentKey, []);
      }

      childrenByParent.get(parentKey).push(item);
    }

    // vPRJ-043:
    // Sorteer BINNEN iedere parent volgens de actieve kopregel-sortering.
    // De boom blijft daardoor intact, maar Nummer / Naam / Bovenliggend /
    // Niveau / Prijs / Actief reageren weer op oplopend-aflopend klikken.
    const sortSiblings = (list) => {
      const dir = sort.dir === "desc" ? -1 : 1;

      return [...list].sort((a, b) => {
        let av = "";
        let bv = "";

        if (sort.key === "full_code") {
          av = a.full_code || a.code || "";
          bv = b.full_code || b.code || "";
        } else if (sort.key === "name") {
          av = a.name || "";
          bv = b.name || "";
        } else if (sort.key === "parent") {
          av = parentNaam(a.parent_id);
          bv = parentNaam(b.parent_id);
        } else if (sort.key === "level") {
          const diff = Number(a.level || 1) - Number(b.level || 1);
          if (diff !== 0) return dir * diff;
        } else if (sort.key === "price") {
          const diff = Number(a.price || 0) - Number(b.price || 0);
          if (diff !== 0) return dir * diff;
        } else if (sort.key === "active") {
          av = a.is_active === false ? "Nee" : "Ja";
          bv = b.is_active === false ? "Nee" : "Ja";
        } else {
          av = a.full_code || a.code || "";
          bv = b.full_code || b.code || "";
        }

        const primary = String(av).localeCompare(String(bv), "nl", {
          numeric: true,
          sensitivity: "base",
        });

        if (primary !== 0) return dir * primary;

        // Bij gelijke waarden blijft de volgorde stabiel en voorspelbaar.
        const byCode = String(a.code || a.full_code || "").localeCompare(
          String(b.code || b.full_code || ""),
          "nl",
          { numeric: true, sensitivity: "base" }
        );

        if (byCode !== 0) return byCode;

        return Number(a.id || 0) - Number(b.id || 0);
      });
    };

    const result = [];
    const visited = new Set();

    const appendBranch = (item, treeLevel) => {
      const key = String(item.id);
      if (visited.has(key)) return;
      visited.add(key);

      result.push({
        ...item,
        _treeLevel: treeLevel,
      });

      if (!expandedIds.has(key)) return;

      const children = sortSiblings(childrenByParent.get(key) || []);
      for (const child of children) {
        appendBranch(child, treeLevel + 1);
      }
    };

    const roots = sortSiblings(childrenByParent.get("") || []);
    for (const root of roots) {
      appendBranch(root, 1);
    }

    // Veiligheidsnet alleen voor echte weesregels:
    // Een normaal kind van een ingeklapte parent mag hier NIET opnieuw
    // als losse hoofdregel worden toegevoegd.
    for (const item of sortSiblings(items)) {
      const parentId = item.parent_id ? String(item.parent_id) : "";
      const heeftGeldigeParent = parentId && byId.has(parentId);

      if (!visited.has(String(item.id)) && !heeftGeldigeParent) {
        appendBranch(item, 1);
      }
    }

    return result;
  }, [
    sorted,
    items,
    expandedIds,
    zoek,
    filterCode,
    filterNaam,
    filterParent,
    filterLaag,
    filterActief,
  ]);

  const gridCount = Math.min(visibleSorted.length, 9999);

  function sorteer(key) {
    setSort((p) => ({
      key,
      dir: p.key === key && p.dir === "asc" ? "desc" : "asc",
    }));
  }

function nlText(raw) {
    let txt = String(raw || "").trim();

    if (!txt) return "";

    txt = txt
      .replace(/\bnieuwe regel\b/gi, "\n")
      .replace(/\bnieuwe alinea\b/gi, "\n\n")
      .replace(/\buitroepteken\b/gi, "!")
      .replace(/\bvraagteken\b/gi, "?")
      .replace(/\bdubbele punt\b/gi, ":")
      .replace(/\bpuntkomma\b/gi, ";")
      .replace(/\bpunt\b/gi, ".")
      .replace(/\bkomma\b/gi, ",")
      .replace(/\s+([.,!?;:])/g, "$1")
      .replace(/([.,!?;:])(?=\S)/g, "$1 ");

    return maakZinnenNetjes(txt);
  }

function handleVoiceCommand(raw) {
    const s = norm(raw);

    if (!s) return false;

    if (
      s === "punt" ||
      s === "komma" ||
      s === "vraagteken" ||
      s === "uitroepteken" ||
      s === "dubbele punt" ||
      s === "puntkomma" ||
      s === "nieuwe regel" ||
      s === "nieuwe alinea"
    ) {
      applyDictationToProjectField(s);
      return true;
    }

    if (s === "stop" || s === "klaar") {
      setSpeechInfo("");
      return true;
    }

    if (s === "nieuw" || s === "nieuwe" || s === "nieuw item") {
      nieuw();
      return true;
    }

    if (s === "vernieuw" || s === "vernieuwen" || s === "refresh") {
      vernieuw();
      return true;
    }

    if (s === "opslaan" || s === "bewaar" || s === "bewaren") {
      opslaan();
      return true;
    }

    if (s === "verwijderen" || s === "delete") {
      verwijderen();
      return true;
    }

    if (s.startsWith("zoek ")) {
      setZoek(raw.replace(/^zoek\s+/i, "").trim());
      setMode("search");
      return true;
    }

    return false;
  }

  function sortMark(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

function kopieerVanBron(bronId) {
    if (!bronId) {
      setForm((p) => ({
        ...p,
        copy_from_id: "",
        copy_children: false,
      }));
      return;
    }

    const bron = items.find((x) => String(x.id) === String(bronId));

    if (!bron) return;

    setForm((p) => ({
      ...p,

      copy_from_id: bronId,

      // NIET kopiëren:
      // id
      // parent_id
      // code
      // name
      // full_code
      // full_name
      // level

      offer_text: bron.offer_text || "",
      internal_text: bron.internal_text || "",
      unit: bron.unit || "",
      price: bron.price ?? "",
      price_incl_vat: priceInclFromExcl(bron.price ?? "", bron.vat_rate || "hoog"),
      vat_rate: bron.vat_rate || "hoog",
      is_active: bron.is_active !== false,
    }));
  }

async function kopieerOnderliggendeOnderdelen({ bronParentId, nieuweParentId }) {
    const bronId = String(bronParentId || "");
    const doelParentId = Number(nieuweParentId || 0);

    if (!bronId || !doelParentId) return;

    const kinderen = items
      .filter((x) => String(x.parent_id || "") === bronId)
      .sort((a, b) =>
        String(a.full_code || a.code || "").localeCompare(
          String(b.full_code || b.code || ""),
          "nl"
        )
      );

    for (const kind of kinderen) {
      const payload = {
        parent_id: doelParentId,
        code: String(kind.code || "").trim(),
        name: String(kind.name || "").trim(),
        offer_text: kind.offer_text || "",
        internal_text: kind.internal_text || "",
        line_type: isAmountLineType(kind.line_type) ? "item" : "text",
        unit: kind.unit || "",
        price: Number(String(kind.price || "0").replace(",", ".")) || 0,
        vat_rate: kind.vat_rate || "hoog",
        is_active: kind.is_active !== false,
        sort_order: Number(kind.sort_order || 0),
      };

      const nieuwKind = await api("/api/projectmanagement", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (nieuwKind?.id) {
        await kopieerOnderliggendeOnderdelen({
          bronParentId: kind.id,
          nieuweParentId: nieuwKind.id,
        });
      }
    }
  }

function openExpandedTextField(fieldName) {
    if (isFormDisabled) return;
    if (fieldName !== "offer_text" && fieldName !== "internal_text") return;

    lastActiveFieldRef.current = fieldName;
    setExpandedTextField(fieldName);

    setTimeout(() => {
      try {
        if (rightPanelRef.current) rightPanelRef.current.scrollTop = 0;
      } catch {}
    }, 0);
  }

  function closeExpandedTextField() {
    const fieldName = expandedTextField;

    // Grote editor sluiten en daarna het normale formulier precies
    // terugzetten bij hetzelfde tekstveld. We geven GEEN focus aan
    // de kleine editor, omdat die anders direct opnieuw vergroot.
    setExpandedTextField("");

    setTimeout(() => {
      try {
        document.activeElement?.blur?.();
      } catch {}

      try {
        const panel = rightPanelRef.current;
        const target =
          fieldName === "internal_text"
            ? interneTekstBlokRef.current
            : offerteTekstBlokRef.current;

        if (!panel || !target) return;

        // Scroll alleen de rechterkolom. Plaats het betreffende tekstblok
        // met een kleine marge bovenin, zodat de gebruiker direct verder kan.
        const panelRect = panel.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - panelRect.top + panel.scrollTop;

        panel.scrollTop = Math.max(0, relativeTop - 12);
      } catch {}
    }, 0);
  }

  function openTekstPopup() {
    setTekstDraft({
      offer_text: form.offer_text || "",
      internal_text: form.internal_text || "",
    });

    setAiResult("");
    setTekstPopupError("");
    setTekstPopupOpen(true);
  }

  function sluitTekstPopup() {
    setTekstPopupOpen(false);
  }

  async function bewaarTekstPopup() {
    if (isReadonly || tekstPopupSaving) return;
    const next = {
      ...form,
      offer_text: tekstDraft.offer_text || "",
      internal_text: tekstDraft.internal_text || "",
    };
    setTekstPopupError("");
    if (!String(next.code || "").trim() || !String(next.name || "").trim()) {
      setTekstPopupError("Vul eerst Nummer/code en Naam in.");
      return;
    }
    if (isAmountLineType(next.line_type) && !richTextPlainText(next.offer_text)) {
      setTekstPopupError("Een artikel moet een zichtbare Offertetekst bevatten.");
      return;
    }
    // Bij bestaande projecten worden de andere velden met hun huidige waarden meegestuurd.
    // Bij nieuwe projecten blijft de bestaande opslagroute voor de projectboom nodig.
    if (!next.id) {
      setTekstPopupError("Sla dit nieuwe projectonderdeel eerst eenmaal op in de rechterkolom. Daarna slaat Opslaan/Sluiten de projectteksten rechtstreeks op.");
      return;
    }
    const exactPrice = (() => {
      if (lastPriceSourceRef.current === "incl") {
        const incl = parsePriceInput(next.price_incl_vat);
        const factor = 1 + vatPercentage(next.vat_rate) / 100;
        if (Number.isFinite(incl)) return Number((factor ? incl / factor : incl).toFixed(6));
      }
      const excl = parsePriceInput(next.price);
      return Number.isFinite(excl) ? Number(excl.toFixed(6)) : 0;
    })();
    const payload = {
      parent_id: next.parent_id ? Number(next.parent_id) : null,
      code: String(next.code).trim(),
      name: String(next.name).trim(),
      offer_text: next.offer_text,
      internal_text: next.internal_text,
      line_type: isAmountLineType(next.line_type) ? "item" : "text",
      unit: isAmountLineType(next.line_type) ? String(next.unit || "").trim() : "",
      price: exactPrice,
      vat_rate: next.vat_rate || "hoog",
      is_active: next.is_active !== false,
      sort_order: Number(next.sort_order || 0),
    };
    setTekstPopupSaving(true);
    try {
      const saved = await api(`/api/projectmanagement/${next.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      const fresh = { ...next, ...(saved && typeof saved === "object" ? saved : {}),
        parent_id: saved?.parent_id ? String(saved.parent_id) : next.parent_id,
        price_incl_vat: priceInclFromExcl(saved?.price ?? exactPrice, saved?.vat_rate || next.vat_rate),
      };
      setForm(fresh);
      originalFormRef.current = { ...fresh };
      setMelding("Projectteksten opgeslagen in database.");
      setTekstPopupOpen(false);
      await laad();
    } catch (e) {
      setTekstPopupError(`Opslaan mislukt: ${e?.message || String(e)}`);
    } finally {
      setTekstPopupSaving(false);
    }
  }

  function buildProjectContext() {
    const currentId = form.id ? String(form.id) : "";

    const childrenByParent = new Map();

    items.forEach((x) => {
      const pid = x.parent_id ? String(x.parent_id) : "";

      if (!childrenByParent.has(pid)) {
        childrenByParent.set(pid, []);
      }

      childrenByParent.get(pid).push(x);
    });

    function walk(parentId, depth = 0) {
      const rows = childrenByParent.get(String(parentId || "")) || [];

      rows.sort((a, b) =>
        String(a.full_code || a.code || "").localeCompare(
          String(b.full_code || b.code || ""),
          "nl"
        )
      );

      const out = [];

      rows.forEach((x) => {
        const indent = "  ".repeat(depth);

        out.push(
          `${indent}- ${x.full_code || x.code || ""} ${x.name || ""} | laag ${x.level || 1} | prijs ${euro(x.price)}`
        );

        if (x.offer_text) {
          out.push(`${indent}  Offertetekst: ${x.offer_text}`);
        }

        if (x.internal_text) {
          out.push(`${indent}  Interne tekst: ${x.internal_text}`);
        }

        out.push(...walk(x.id, depth + 1));
      });

      return out;
    }

    return [
      `Project: ${form.full_code || form.code || ""} ${form.name || ""}`,
      "",
      "Projectstructuur:",
      ...walk(currentId, 1),
    ].join("\n");
  }

  function appendAiConversation(question, answer) {
    const block =
      `Vraag:\n${question}\n\n` +
      `----------------------------------------\n\n` +
      `Antwoord:\n${answer}\n\n` +
      `========================================\n\n`;

    setAiText((prev) => {
      const cur = String(prev || "").trim();

      if (!cur) return block;

      return `${cur}\n\n${block}`;
    });
  }

  function openAiProject() {
    setAiSource("page");
    setAiOpen(true);
    setAiText("");
    setAiResult("");

    setTimeout(() => {
      try {
        aiTextRef.current?.focus?.({ preventScroll: true });
      } catch {
        try {
          aiTextRef.current?.focus?.();
        } catch {}
      }
    }, 0);
  }

  async function controleerProjectMetAi() {
    setAiBusy(true);
    setAiOpen(true);
    setAiText("AI controleert projectstructuur...");

    try {
      const prompt = `
Je controleert projectbeheer / projectstructuur in StaffIQ.

Controleer:
1. Nummering en hiërarchie
2. Logische opbouw
3. Ontbrekende deelprojecten
4. Duidelijkheid van projectteksten
5. Mogelijke verbeteringen

${buildProjectContext()}

Geef antwoord in duidelijk Nederlands.
      `.trim();

      const res = await api("/api/ai/organisations/check", {
        method: "POST",
        body: JSON.stringify({
          mode: "chat",
          question: "Controleer projectbeheer",
          organisation: {
            module: "Projectbeheer",
            code: form.full_code || form.code || "",
            name: form.name || "",
          },
          prompt,
        }),
      });

      setAiText(res?.result || "Geen AI-antwoord ontvangen.");
    } catch (e) {
      setAiText("FOUT:\n" + (e?.message || String(e)));
    } finally {
      setAiBusy(false);
    }
  }

  async function runAiChat() {
    const question = String(aiText || "").trim();

    if (!question) {
      setAiText("Typ eerst een vraag voor Chat met AI.");
      return;
    }

    setAiBusy(true);

    try {
      const prompt = `
Je bent een assistent voor StaffIQ.

Beantwoord de vraag op basis van dit project:

${buildProjectContext()}

Vraag:
${question}
      `.trim();

      const res = await api("/api/ai/organisations/check", {
        method: "POST",
        body: JSON.stringify({
          mode: "chat",
          question,
          organisation: {
            module: "Projectbeheer",
            code: form.full_code || form.code || "",
            name: form.name || "",
          },
          prompt,
        }),
      });

      appendAiConversation(question, res?.result || "Geen AI-antwoord ontvangen.");
    } catch (e) {
      appendAiConversation(question, "FOUT: " + (e?.message || String(e)));
    } finally {
      setAiBusy(false);
    }
  }

  function sluitAiPopup() {
    setAiOpen(false);
    setAiBusy(false);
  }

async function controleerTekstMetAi() {
    setAiBusy(true);
    setAiOpen(true);
    setAiSource("popup");
    setAiText("AI controleert projectteksten...");

    try {
      const tekst = [
        `Projectonderdeel: ${form.full_code || form.code || ""} ${form.name || ""}`,
        "",
        "Offertetekst:",
        tekstDraft.offer_text || "",
        "",
        "Interne tekst:",
        tekstDraft.internal_text || "",
      ].join("\n");

      const res = await api("/api/ai/organisations/check", {
        method: "POST",
        body: JSON.stringify({
          mode: "check",
          organisation: {
            module: "Projectbeheer",
            code: form.full_code || form.code || "",
            name: form.name || "",
          },
          prompt: `
Controleer deze projecttekst op:
- duidelijkheid
- professionaliteit
- volledigheid
- verbeterpunten

${tekst}
          `.trim(),
        }),
      });

      setAiText(res?.result || "Geen AI-antwoord ontvangen.");
    } catch (e) {
      setAiText("FOUT:\n" + (e?.message || String(e)));
    } finally {
      setAiBusy(false);
    }
  }

function aiButtonContent(label = "AI") {
    return <>✨ {label}</>;
  }

function tekstPopupButtons() {
    const popupButtonBase = isMobile
      ? {
          ...buttonStyle,
          padding: "8px 4px",
          fontSize: 11,
          minWidth: 0,
          width: "100%",
          whiteSpace: "nowrap",
          textAlign: "center",
        }
      : {
          ...buttonStyle,
          minWidth: 0,
          whiteSpace: "nowrap",
        };

    const popupDisabledButton = {
      ...popupButtonBase,
      opacity: 0.5,
      cursor: "not-allowed",
    };

    const popupBlueButton = {
      ...popupButtonBase,
      background: "#2f7fd1",
      color: "white",
      borderColor: "#2f7fd1",
    };

    const popupAiButton = {
      ...popupButtonBase,
      background: "#6f42c1",
      borderColor: "#6f42c1",
      color: "white",
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "0.9fr 0.8fr 1.45fr 1.2fr"
            : "auto auto auto auto",
          gap: isMobile ? 5 : 8,
          justifyContent: isMobile ? "stretch" : "end",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            detectActiveProjectField();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            detectActiveProjectField();
          }}
          onClick={startSpeak}
          disabled={!voice.supported || voice.listening}
          style={
            !voice.supported || voice.listening
              ? popupDisabledButton
              : popupButtonBase
          }
        >
          {voice.listening ? "🎤..." : "Spreek"}
        </button>

        <button
          onClick={controleerTekstMetAi}
          disabled={aiBusy}
          style={aiBusy ? popupDisabledButton : popupAiButton}
        >
          {aiBusy ? "..." : "✨ AI"}
        </button>

        <button
          onClick={bewaarTekstPopup}
          disabled={isReadonly || tekstPopupSaving}
          style={isReadonly ? popupDisabledButton : popupBlueButton}
        >
          {tekstPopupSaving ? "Opslaan..." : "Opslaan/Sluiten"}
        </button>

        <button onClick={sluitTekstPopup} style={popupButtonBase}>
          Annuleren
        </button>
      </div>
    );
  }

  const parentOptiesVoorForm = items.filter(
    (x) => String(x.id) !== String(form.id || "")
  );

const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 900;

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    width: "100%",
    padding: "7px 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
    fontSize: 13,
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: 13,
  };

  const disabledButton = {
    ...buttonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
  };

  const blueButton = {
    ...buttonStyle,
    background: "#2f7fd1",
    color: "white",
    borderColor: "#2f7fd1",
  };

  const redButton = {
    ...buttonStyle,
    background: "#b33333",
    color: "white",
    borderColor: "#b33333",
  };

  const th = {
    padding: "6px 8px",
    textAlign: "left",
    borderBottom: "1px solid #eee",
    background: "#fafafa",
    whiteSpace: "nowrap",
    fontSize: 13,
    lineHeight: 1.2,
  };

  const td = {
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
    whiteSpace: "nowrap",
    fontSize: 13,
    lineHeight: 1.2,
  };

  const labelStyle = {
    fontSize: 13,
    color: "#333",
    marginBottom: 3,
    display: "block",
  };

return (
    <div ref={pageRef}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : `minmax(0, 1fr) minmax(${RIGHT_WIDTH}px, ${RIGHT_WIDTH}px)`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>

<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  }}
>
  <h1 style={{ margin: 0 }}>Projectbeheer</h1>
  <span style={{ fontSize: 12, opacity: 0.55 }}>{VERSION}</span>

  <HelpButton
    topic="projectbeheer"
    title="Help over Projectbeheer"
  />
</div>

{!isMobile ? (
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <input
                ref={zoekRef}
                value={zoek}
                onChange={(e) => {
                  setZoek(e.target.value);
                  setMode("search");
                  lastActiveFieldRef.current = "zoek";
                }}
                onFocus={() => {
                  setMode("search");
                  lastActiveFieldRef.current = "zoek";
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  detectActiveProjectField();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  detectActiveProjectField();
                }}
                onClick={startSpeak}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? disabledButton : buttonStyle}
                title="1x klikken = 1 commando of invultekst dicteren"
              >
                {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
              </button>

              <button onClick={nieuw} disabled={isReadonly} style={isReadonly ? disabledButton : buttonStyle}>
                ➕ Nieuw
              </button>

              <button onClick={vernieuw} style={buttonStyle}>
                🔄 Vernieuw
              </button>

              <button
                onClick={openAiProject}
                disabled={aiBusy || !form.id}
                style={aiBusy || !form.id ? disabledButton : buttonStyle}
                title={!form.id ? "Selecteer eerst een projectregel" : "AI Projectbeheer"}
              >
                {aiButtonContent("AI")}
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={zoekRef}
                  value={zoek}
                  onChange={(e) => {
                    setZoek(e.target.value);
                    setMode("search");
                    lastActiveFieldRef.current = "zoek";
                  }}
                  onFocus={() => {
                    setMode("search");
                    lastActiveFieldRef.current = "zoek";
                  }}
                  placeholder="Zoek..."
                  style={{
                    ...searchInputStyle,
                    width: "100%",
                    minWidth: 0,
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    detectActiveProjectField();
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    detectActiveProjectField();
                  }}
                  onClick={startSpeak}
                  disabled={!voice.supported || voice.listening}
                  style={{
                    ...(!voice.supported || voice.listening ? disabledButton : buttonStyle),
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                  title="1x klikken = 1 commando of invultekst dicteren"
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>

                <button
                  onClick={nieuw}
                  disabled={isReadonly}
                  style={{
                    ...(isReadonly ? disabledButton : buttonStyle),
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                >
                  ➕ Nieuw
                </button>

                <button
                  onClick={vernieuw}
                  style={{
                    ...buttonStyle,
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                >
                  🔄 Vernieuw
                </button>

                <button
                  onClick={openAiProject}
                  disabled={aiBusy || !form.id}
                  style={{
                    ...(aiBusy || !form.id ? disabledButton : buttonStyle),
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                  title={!form.id ? "Selecteer eerst een projectregel" : "AI Projectbeheer"}
                >
                  {aiButtonContent("AI")}
                </button>
              </div>
            </>
          )}

          {speechInfo && <div style={{ color: "#555", marginBottom: 8 }}>{speechInfo}</div>}
          {!hideVoiceError && voice.error && (
            <div style={{ color: "crimson", marginBottom: 8 }}>{String(voice.error)}</div>
          )}

          {melding && <div style={{ color: "green", marginBottom: 8 }}>{melding}</div>}
          {fout && <div style={{ color: "crimson", marginBottom: 8 }}>{fout}</div>}

          <div
            ref={midScrollRef}
            style={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: isMobile ? "none" : "calc(100vh - 135px)",
              background: "white",
              borderRadius: 8,
              border: "1px solid #eee",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: TABLE_MIN_WIDTH }}>
              <thead>
                <tr>
                  <th style={th} onClick={() => sorteer("full_code")}>Nummer{sortMark("full_code")}</th>
                  <th style={th} onClick={() => sorteer("name")}>Naam{sortMark("name")}</th>
                  <th style={th} onClick={() => sorteer("parent")}>Bovenliggend{sortMark("parent")}</th>
                  <th style={th} onClick={() => sorteer("level")}>Laag{sortMark("level")}</th>
                  <th style={th} onClick={() => sorteer("price")}>Prijs excl. BTW{sortMark("price")}</th>
                  <th style={th} onClick={() => sorteer("active")}>Actief{sortMark("active")}</th>
                </tr>

                <tr>
                  <th style={th}>
                    <select value={filterCode} onChange={(e) => setFilterCode(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesCode.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}>
                    <select value={filterNaam} onChange={(e) => setFilterNaam(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesNaam.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}>
                    <select value={filterParent} onChange={(e) => setFilterParent(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesParent.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}>
                    <select value={filterLaag} onChange={(e) => setFilterLaag(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesLaag.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}></th>

                  <th style={th}>
                    <select value={filterActief} onChange={(e) => setFilterActief(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      <option value="Ja">Ja</option>
                      <option value="Nee">Nee</option>
                    </select>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} style={td}>Laden...</td>
                  </tr>
                )}

                {!loading && visibleSorted.length === 0 && (
                  <tr>
                    <td colSpan={6} style={td}>Geen projectonderdelen gevonden.</td>
                  </tr>
                )}

                {!loading && visibleSorted.map((x) => (
                  <tr
                    key={x.id}
                    onClick={() => selecteer(x)}
                    style={{
                      background: String(selectedId) === String(x.id) ? "#e7f1ff" : "white",
                      cursor: "pointer",
                    }}
                  >
                    <td style={td}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          paddingLeft: Math.max(0, Number(x._treeLevel || x.level || 1) - 1) * 18,
                          minWidth: 0,
                        }}
                      >
                        {hasChildren(x.id) ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleExpanded(x.id);
                            }}
                            aria-label={
                              expandedIds.has(String(x.id))
                                ? "Project inklappen"
                                : "Project uitklappen"
                            }
                            title={
                              expandedIds.has(String(x.id))
                                ? "Onderliggende onderdelen inklappen"
                                : "Onderliggende onderdelen uitklappen"
                            }
                            style={{
                              width: 22,
                              minWidth: 22,
                              height: 22,
                              padding: 0,
                              marginRight: 3,
                              border: "none",
                              background: "transparent",
                              color: "#1f4e79",
                              cursor: "pointer",
                              fontSize: 14,
                              lineHeight: "22px",
                              textAlign: "center",
                            }}
                          >
                            {expandedIds.has(String(x.id)) ? "▼" : "▶"}
                          </button>
                        ) : (
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: 22,
                              minWidth: 22,
                              marginRight: 3,
                            }}
                          />
                        )}

                        <span style={{ fontWeight: 600 }}>
                          {x.full_code || x.code}
                        </span>
                      </div>
                    </td>
                    <td style={td}>{x.name}</td>
                    <td style={td}>{parentNaam(x.parent_id) || "-"}</td>
                    <td style={td}>{x._treeLevel || x.level || 1}</td>
                    <td style={{ ...td, textAlign: "right" }}>{euro(x.price)}</td>
                    <td style={td}>{x.is_active === false ? "Nee" : "Ja"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

<div
          ref={rightPanelRef}
          style={{
            background: "#0b4f73",
            borderRadius: 10,
            padding: 10,
            position: isMobile ? "static" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 14,
              boxSizing: "border-box",
              fontSize: 13,
            }}
          >
            {expandedTextField ? (
              <>
                <h2
                  style={{
                    marginTop: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span>{expandedTextField === "offer_text" ? "Offertetekst" : "Interne tekst"}</span>
                  <button
                    type="button"
                    onClick={closeExpandedTextField}
                    style={buttonStyle}
                    title="Terug naar normaal formulier"
                  >
                    ↩ Terug
                  </button>
                </h2>

                <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>
                  {expandedTextField === "offer_text"
                    ? "Offertetekst bewerken"
                    : "Interne tekst / werkinstructie bewerken"}
                </div>

                <RichTextEditor
                  value={expandedTextField === "offer_text" ? form.offer_text || "" : form.internal_text || ""}
                  onActivate={() => {
                    lastActiveFieldRef.current = expandedTextField;
                  }}
                  onChange={(value) =>
                    setForm((p) => ({
                      ...p,
                      [expandedTextField]: value,
                    }))
                  }
                  disabled={isFormDisabled}
                  minHeight={isMobile ? 420 : "60vh"}
                />

                <div
                  style={{
                    background: "white",
                    paddingTop: 10,
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    flexWrap: "wrap",
                    borderTop: "1px solid #eee",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeExpandedTextField}
                    style={buttonStyle}
                    title="Terug naar normaal formulier; wijzigingen blijven behouden"
                  >
                    ↩ Terug
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ marginTop: 0 }}>Projectbeheer</h2>

                <div style={{ display: "grid", gap: 10 }}>
              <label>
                <span style={labelStyle}>Bovenliggend onderdeel</span>
<select
                  value={form.parent_id || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "parent";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, parent_id: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="">(geen - hoofdniveau)</option>
                  {parentOptiesVoorForm.map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.full_code || p.code || ""} ${p.name || ""}`.trim()}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>Nummer/code</span>
                <input
                  ref={codeRef}
                  value={form.code || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "code";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                  placeholder="Bijv. 01"
                />
              </label>

              <label>
                <span style={labelStyle}>Naam</span>
                <input
                  ref={naamRef}
                  value={form.name || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "naam";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                  placeholder="Bijv. Vloer renovatie"
                />
              </label>

<label>
                <span style={labelStyle}>Kopie van</span>

                <select
                  value={form.copy_from_id || ""}
                  onChange={(e) => kopieerVanBron(e.target.value)}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="">(geen kopie)</option>

                  {items.map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.full_code || x.code || ""} ${x.name || ""}`.trim()}
                    </option>
                  ))}
                </select>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: -4,
                  opacity: form.copy_from_id ? 1 : 0.55,
                  cursor:
                    isFormDisabled || !form.copy_from_id
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.copy_children === true}
                  disabled={isFormDisabled || !form.copy_from_id}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      copy_children: e.target.checked,
                    }))
                  }
                />
                <span style={{ fontSize: 13 }}>
                  Onderliggende onderdelen ook kopiëren
                </span>
              </label>

              <div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>Volledig nummer</div>
                <strong>{form.full_code || "(na opslaan)"}</strong>
              </div>

              <div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>Volledige naam</div>
                <strong>{form.full_name || "(na opslaan)"}</strong>
              </div>

              <label>
                <span style={labelStyle}>Regeltype</span>
                <select
                  value={isAmountLineType(form.line_type) ? "item" : "text"}
                  onChange={(e) => {
                    const lineType = e.target.value;
                    setForm((p) => ({
                      ...p,
                      line_type: lineType,
                      price_incl_vat: isAmountLineType(lineType)
                        ? priceInclFromExcl(p.price, p.vat_rate)
                        : "",
                    }));
                  }}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="item">Artikel</option>
                  <option value="text">Tekst</option>
                </select>
              </label>

              <label>
                <span style={labelStyle}>Eenheid</span>
                <input
                  value={form.unit || ""}
                  maxLength={30}
                  onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
                  disabled={isFormDisabled || !isAmountLineType(form.line_type)}
                  style={{ ...inputStyle, background: !isAmountLineType(form.line_type) ? "#f4f4f4" : "white" }}
                  placeholder={isAmountLineType(form.line_type) ? "Bijv. m², m¹, st, uur" : "Niet van toepassing"}
                />
              </label>

              <label>
                <span style={labelStyle}>Prijs exclusief BTW</span>
                <input
                  ref={prijsRef}
                  value={form.price ?? ""}
                  inputMode="decimal"
                  onFocus={() => {
                    lastActiveFieldRef.current = "prijs";
                  }}
                  onChange={(e) => {
                    lastPriceSourceRef.current = "excl";
                    const value = e.target.value;
                    setForm((p) => ({
                      ...p,
                      price: value,
                      price_incl_vat: priceInclFromExcl(value, p.vat_rate),
                    }));
                  }}
                  onBlur={(e) => {
                    lastPriceSourceRef.current = "excl";
                    const formatted = formatPriceInput(e.target.value);
                    setForm((p) => ({
                      ...p,
                      price: formatted,
                      price_incl_vat: priceInclFromExcl(formatted, p.vat_rate),
                    }));
                  }}
                  disabled={isFormDisabled || !isAmountLineType(form.line_type)}
                  style={{
                    ...inputStyle,
                    background: !isAmountLineType(form.line_type) ? "#f4f4f4" : inputStyle.background,
                  }}
                  placeholder={isAmountLineType(form.line_type) ? "0,00" : "Niet van toepassing"}
                />
              </label>

              <label>
                <span style={labelStyle}>Btw</span>
                <select
                  value={form.vat_rate || "hoog"}
                  onChange={(e) => {
                    lastPriceSourceRef.current = "excl";
                    const vatRate = e.target.value;
                    setForm((p) => ({
                      ...p,
                      vat_rate: vatRate,
                      price_incl_vat: priceInclFromExcl(p.price, vatRate),
                    }));
                  }}
                  disabled={isFormDisabled || !isAmountLineType(form.line_type)}
                  style={{
                    ...inputStyle,
                    background: !isAmountLineType(form.line_type) ? "#f4f4f4" : inputStyle.background,
                  }}
                >
                  <option value="hoog">21%</option>
                  <option value="laag">9%</option>
                  <option value="0">0%</option>
                  <option value="verlegd">Verlegd</option>
                </select>
              </label>

              <label>
                <span style={labelStyle}>Prijs inclusief BTW</span>
                <input
                  ref={prijsInclRef}
                  value={form.price_incl_vat ?? ""}
                  inputMode="decimal"
                  onFocus={() => {
                    lastActiveFieldRef.current = "prijs_incl";
                  }}
                  onChange={(e) => {
                    lastPriceSourceRef.current = "incl";
                    const value = e.target.value;
                    setForm((p) => ({
                      ...p,
                      price_incl_vat: value,
                      price: priceExclFromIncl(value, p.vat_rate),
                    }));
                  }}
                  onBlur={(e) => {
                    lastPriceSourceRef.current = "incl";
                    const formatted = formatPriceInput(e.target.value);
                    setForm((p) => ({
                      ...p,
                      price_incl_vat: formatted,
                      price: priceExclFromIncl(formatted, p.vat_rate),
                    }));
                  }}
                  disabled={isFormDisabled || !isAmountLineType(form.line_type)}
                  style={{
                    ...inputStyle,
                    background: !isAmountLineType(form.line_type) ? "#f4f4f4" : inputStyle.background,
                  }}
                  placeholder={isAmountLineType(form.line_type) ? "0,00" : "Niet van toepassing"}
                />
              </label>

              <label>
                <span style={labelStyle}>Actief</span>
                <select
                  value={form.is_active === false ? "false" : "true"}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === "true" }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="true">Ja</option>
                  <option value="false">Nee</option>
                </select>
              </label>

              <button
                onClick={openTekstPopup}
                disabled={isFormDisabled}
                style={isFormDisabled ? disabledButton : blueButton}
              >
                Projectteksten bewerken
              </button>

              <div ref={offerteTekstBlokRef}>
                <span style={labelStyle}>Offertetekst</span>
                <RichTextEditor
                  externalRef={offerteTekstRef}
                  value={form.offer_text || ""}
                  onActivate={() => openExpandedTextField("offer_text")}
                  onChange={(value) => setForm((p) => ({ ...p, offer_text: value }))}
                  disabled={isFormDisabled}
                  minHeight={110}
                />
              </div>

              <div ref={interneTekstBlokRef}>
                <span style={labelStyle}>Interne tekst</span>
                <RichTextEditor
                  externalRef={interneTekstRef}
                  value={form.internal_text || ""}
                  onActivate={() => openExpandedTextField("internal_text")}
                  onChange={(value) => setForm((p) => ({ ...p, internal_text: value }))}
                  disabled={isFormDisabled}
                  minHeight={95}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gap: 8,
                  marginTop: 10,
                  alignItems: "start",
                  justifyContent: "start",
                }}
              >
                <button
                  onClick={() => opslaan()}
                  disabled={isFormDisabled}
                  style={{ ...(isFormDisabled ? disabledButton : blueButton), width: "100%" }}
                >
                  💾 Opslaan
                </button>

                <button
                  onClick={() => opslaan({ closeAfter: true })}
                  disabled={isFormDisabled}
                  style={{ ...(isFormDisabled ? disabledButton : blueButton), width: "100%" }}
                >
                  Opslaan/Sluiten
                </button>

                <button
                  onClick={verwijderen}
                  disabled={isReadonly || !form.id}
                  style={{ ...((isReadonly || !form.id) ? disabledButton : redButton), width: "100%" }}
                >
                  🗑 Verwijderen
                </button>

                <button
                  onClick={annuleren}
                  disabled={isFormLocked}
                  style={{
                    ...(isFormLocked ? disabledButton : buttonStyle),
                    width: "100%",
                  }}
                >
                  Annuleren
                </button>
              </div>

              <div style={{ fontSize: 12, opacity: 0.65 }}>
                Projectbeheer-id: {form.id || "(nieuw)"}
              </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

{aiOpen && (
        <div
          onClick={sluitAiPopup}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 10,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: isMobile ? "96vw" : 980,
              height: isMobile ? "88vh" : "90vh",
              overflow: "hidden",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              padding: 16,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "#fff",
                paddingBottom: 10,
                marginBottom: 12,
                borderBottom: "1px solid #ddd",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <h2 style={{ margin: 0 }}>
                {aiSource === "popup" ? "AI Projectteksten" : "AI Projectbeheer"}
              </h2>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={sluitAiPopup} disabled={aiBusy} style={buttonStyle}>
                  Sluiten
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 12, fontWeight: 700 }}>
              Kies een actie:
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <button
                onClick={controleerProjectMetAi}
                disabled={aiBusy}
                style={aiBusy ? disabledButton : buttonStyle}
              >
                Project controleren
              </button>

              <button
                onClick={runAiChat}
                disabled={aiBusy}
                style={aiBusy ? disabledButton : buttonStyle}
              >
                Chat met AI
              </button>
            </div>

            <div style={{ marginBottom: 8, fontWeight: 700, flex: "0 0 auto" }}>
              AI venster
            </div>

            <textarea
              ref={aiTextRef}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Klik op een AI-keuze, of typ hier je vraag voor Chat met AI."
              style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                height: "100%",
                padding: 12,
                border: "1px solid #ccc",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 15,
                lineHeight: 1.45,
                boxSizing: "border-box",
                resize: "none",
                whiteSpace: "pre-wrap",
              }}
            />
          </div>
        </div>
      )}

      {tekstPopupOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
<div
            style={{
              width: isMobile ? "calc(100vw - 28px)" : "min(1000px, 96vw)",
              maxWidth: "100%",
              maxHeight: "92vh",
              overflow: "auto",
              background: "white",
              borderRadius: 10,
              padding: isMobile ? 14 : 18,
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              boxSizing: "border-box",
            }}
          >
<div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
                gap: isMobile ? 10 : 12,
                alignItems: "center",
                marginBottom: 12,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <h2 style={{ margin: 0 }}>Projectteksten</h2>
              {tekstPopupButtons()}
            </div>

            {tekstPopupError && <div role="alert" style={{ color: "#b33333", marginBottom: 10 }}>{tekstPopupError}</div>}
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <span style={labelStyle}>Offertetekst</span>
                <RichTextEditor
                  value={tekstDraft.offer_text || ""}
                  onActivate={() => { lastActiveFieldRef.current = "popup_offer_text"; }}
                  onChange={(value) => setTekstDraft((p) => ({ ...p, offer_text: value }))}
                  disabled={isReadonly}
                  minHeight={260}
                />
              </div>

              <div>
                <span style={labelStyle}>Interne tekst / werkinstructie</span>
                <RichTextEditor
                  value={tekstDraft.internal_text || ""}
                  onActivate={() => { lastActiveFieldRef.current = "popup_internal_text"; }}
                  onChange={(value) => setTekstDraft((p) => ({ ...p, internal_text: value }))}
                  disabled={isReadonly}
                  minHeight={180}
                />
              </div>

{tekstPopupButtons()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}